import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTransportMutation, useTransportQueryUtils } from "@/transport"
import { toast } from "@coval/ui"

import type { UpdateOrganizationTagRequest } from "@/types_api/backend/transport/update_organization_tag_request"

import { TAGGED_RESOURCE_OPERATIONS } from "../constants/tagged-resource-operations"
import { buildOrganizationTagsByName } from "../utils/tagCatalog"
import { normalizeTag } from "../utils/tagNormalization"
import { useOrganizationTags } from "./useOrganizationTags"

export function useUpdateOrganizationTagColor() {
  const { tags } = useOrganizationTags()
  const queryUtils = useTransportQueryUtils()
  const tagsByName = useMemo(() => buildOrganizationTagsByName(tags), [tags])
  const mutation = useTransportMutation("updateOrganizationTag")
  const scopeKey = JSON.stringify(queryUtils.getKey("listOrganizationTags"))
  const generation = useRef(0)
  const pending = useRef(false)
  const [isPending, setIsPending] = useState(false)

  useEffect(() => {
    generation.current += 1
    pending.current = false
    setIsPending(false)
    return () => {
      generation.current += 1
    }
  }, [scopeKey])

  const update = useCallback(
    async (request: UpdateOrganizationTagRequest) => {
      if (pending.current || !mutation.isReady) {
        return false
      }
      const currentGeneration = generation.current
      pending.current = true
      setIsPending(true)
      let saved = false
      try {
        await mutation.mutateAsync(request)
        saved = true
      } catch (error) {
        if (generation.current === currentGeneration) {
          toast.error({
            title: "Tag could not be updated",
            error,
            fallback: "Please try again.",
          })
        }
      } finally {
        // Resource records embed names, while colors come from the catalogs.
        // Ambiguous failures still refresh every potentially affected reader.
        try {
          await Promise.all([
            queryUtils.invalidateOperation("listOrganizationTags"),
            queryUtils.invalidateOperation("listWorkspaceTags"),
            ...(request.name != null || !saved
              ? TAGGED_RESOURCE_OPERATIONS.map((operation) =>
                  queryUtils.invalidateOperation(operation),
                )
              : []),
          ])
        } catch (error) {
          if (generation.current === currentGeneration) {
            toast.error({
              title: "Tags could not be refreshed",
              error,
              fallback: "Reload before editing again.",
            })
          }
        }
        if (generation.current === currentGeneration) {
          pending.current = false
          setIsPending(false)
        }
      }
      return saved && generation.current === currentGeneration
    },
    [mutation, queryUtils],
  )

  const updateTagColor = useCallback(
    (tagName: string, color: string) => {
      const tag = tagsByName[normalizeTag(tagName)]
      if (tag) {
        void update({ tag_id: tag.id, color })
      }
    },
    [tagsByName, update],
  )

  const updateTagName = useCallback(
    async (tagName: string, name: string) => {
      const tag = tagsByName[normalizeTag(tagName)]
      const normalizedName = normalizeTag(name)
      if (!tag || !normalizedName) {
        return false
      }
      return update({ tag_id: tag.id, name: normalizedName })
    },
    [tagsByName, update],
  )

  return { updateTagColor, updateTagName, isPending, error: mutation.error }
}
